import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import _, {
  get,
  isEqual,
  map,
  isEmpty,
  includes,
  filter,
  find,
  slice,
  assign,
  every,
  orderBy,
  pick,
  mapKeys,
  has
} from 'lodash';
import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import StepsBar from '../../../components/StepsBar/StepsBar';
import {Order, ReduxState, SelectOption, State} from '../../../interface';
import style from './AssetsMapFile.module.scss';
import {
  cancelUploadFile,
  getAssetsTemplate,
  getErrorMessage,
  getUploadFile,
  updateUploadFile
} from '../../../services';
import exportXLSX from '../../../utils/exportFile';
import {connect} from 'react-redux';
import useMappedResult from '../../../hook/useMappedResult';
import useReturnJSON from '../../../hook/useReturnJSON';
import useFileToInferAssetsFields from '../../../hook/useFileToInferAssetsFields';
import classNames from 'classnames';
import FieldSelect from '../../../components/FieldSelect/FieldSelect';
import chooseTemplate from '../../../utils/chooseTemplate';
import {
  ASSETFILETYPE,
  FIXEDASSETFILECOLUMNS,
  INVENTORYFILECOLUMNS,
  LEASEEQUIPMENTFILECOLUMNS,
  LEASEVEHICLEFILECOLUMNS,
  VEHICLEFILECOLUMNS,
} from '../../../constants';
import usePrevious from '../../../hook/usePrevious';
import useFileToAssets from '../../../hook/useFileToAssets';
import useProperties from '../../../hook/useProperties';
import formatAssets from '../../../utils/formatAssets';
import {Dispatch} from 'redux';
import actions from '../../../redux/actions';
import TooltipWrapper from '../../../components/TooltipWrapper/TooltipWrapper';
import {TOOLTIP_SECTIONS} from '../../../enums';
import Loader, {LoaderIndicator} from '../../../components/Loader/Loader';
import ToastMessage, {
  ToastMessageRefType,
} from '../../../components/ToastMessage/ToastMessage';

interface Props {
  states: State[];
  uploadAssetsFile: File;
  setUploadAssetsFile: (value: File | undefined) => void;
}

interface ExtFile {
  status: string;
  mappingPriority: string;
  propertyData: string;
  expectData: string;
  mapFileColumn: SelectOption[];
  fileColumnPreview?: string[];
  selectedColumn: string;
}

function AssetsMapFile(props: Readonly<Props>) {
  const {uploadAssetsFile, states, setUploadAssetsFile} = props;

  const navigate = useNavigate();
  const {id} = useParams();
  const {properties, loading: loadingProperties } = useProperties();

  const [type, setType] = useState<string>(
    sessionStorage.getItem('assetType') as string,
  );

  const {fileColumns, setFileColumns} = useMappedResult(uploadAssetsFile);
  const {fileJSON: returnJSON, setfileJSON} = useReturnJSON(uploadAssetsFile);
  const {inferedAssets, setInferedAssets} = useFileToInferAssetsFields(
    returnJSON,
    type,
    properties,
  );

  const initialAssets = inferedAssets;

  const { assets: assetsFromFile } = useFileToAssets(
    initialAssets,
    type,
    properties,
  );

  const [file, setfile] = useState<ExtFile[]>([]);
  const [order, setorder] = useState<Order>(false);
  const [orderkey, setorderkey] = useState<string>('');
  const [validate, setValidate] = useState<boolean>(false);
  const [cancellingUploadFile, setCancellingUploadFile] =
    useState<boolean>(false);
  const [downloading, setDownloading] = useState(false);

  // Breadcrumb, dashboard depend on user skip or not, property link depend where is the page from
  const breadcrumb = [
    {
      value: 'Assets',
      href: '/assets',
    },
    {value: 'Add Asset', href: '/assets/assetsUpload'},
    {value: 'File Mapping'},
  ];

  useEffect(() => {
    if (!uploadAssetsFile?.name || isEmpty(fileColumns)) return;

    const mapped = map(chooseTemplate(type), (item) => ({
      ...item,
      status: includes(fileColumns, item.propertyData) ? 'Mapped' : 'Unmapped',
      selectedColumn: includes(fileColumns, item.propertyData)
        ? item.propertyData
        : '',
    })) as ExtFile[];

    setfile((prev) => (isEqual(prev, mapped) ? prev : mapped));

    if (uploadAssetsFile.name) setFileName(uploadAssetsFile.name);
    setUploadAssetsFile(undefined);
  }, [type, uploadAssetsFile?.name, fileColumns])

  useEffect(() => {
    setValidate(
      every(
        filter(file, (f) => {
          return includes(
            filter(chooseTemplate(type), ['mappingPriority', 'Required']).map(
              (item) => item.propertyData,
            ),
            f['propertyData'],
          );
        }),
        (o) => isEqual(o['status'], 'Mapped'),
      ),
    );
  }, [file]);

  // Order the properties list
  useEffect(() => {
    setfile(orderBy(file, [orderkey], [order]));
  }, [order, orderkey]);
  // Change order when click on the table header
  const changeOrderHandle = (key: string) => {
    if (isEqual(key, orderkey)) {
      switch (order) {
        case false:
          setorder('asc');
          break;
        case 'asc':
          setorder('desc');
          break;
        default:
          setorder(false);
          break;
      }
    } else {
      setorderkey(key);
      setorder('asc');
    }
  };

  const download = () => {
    const res = getAssetsTemplate(type);
    setDownloading(true);
    exportXLSX(res, () => {
      setDownloading(false);
    });
  };

  const refFile = usePrevious(file);
  const refInitialAssets = usePrevious(initialAssets);
  const toastRef = useRef<ToastMessageRefType | null>(null);

  const [updatingUploadFile, setUpdatingUploadFile] = useState<boolean>(false);
  const [movingNextStep, setMovingNextStep] = useState<boolean>(false);
  const [initial, setInitial] = useState<boolean>(true);
  const [first, setFirst] = useState<boolean>(true);

  const [fileName, setFileName] = useState<string>('');
  const [fetchingUploadFile, setFetchingUploadFile] = useState(false);

  const sendUpdatedFile = useCallback(async () => {
    try {
      setUpdatingUploadFile(true);

      const data = await updateUploadFile(id as string, {
        columnMappings: [fileColumns, initialAssets, file],
        status: 'In Progress',
      });

      const errorMessage = getErrorMessage(data);
      if (errorMessage)
        throw new Error(errorMessage);

      if (!first)
        toastRef.current?.showSuccessToast('Update successfully');

      setFirst(false);
    } catch (error) {
      if (error instanceof Error) {
        toastRef.current?.showErrorToast(error.message);
      }
    } finally {
      setUpdatingUploadFile(false);
    }
  }, [id, file, initialAssets, fileColumns, first]);

  useEffect(() => {
    const isDataReadyToUpdate = (
      !isEqual(refFile, file) ||
      !isEqual(refInitialAssets, initialAssets)
    );
    const isRequiredDataFilled = (
      !isEmpty(fileColumns) &&
      !isEmpty(initialAssets) &&
      !isEmpty(file)
    );

    if (id) {
      if (initial) {
        if (isRequiredDataFilled) {
          if (isDataReadyToUpdate) {
            sendUpdatedFile();
          }
        }
      } else {
        setInitial(true);
      }
    }
  }, [id, file, fileColumns, initial, initialAssets, sendUpdatedFile]);

  useEffect(() => {
    if (id) {
      if (isEmpty(uploadAssetsFile.name)) {
        setFetchingUploadFile(true);
        getUploadFile(id)
          .then((d) => {
            setFileColumns(d.data.columnMappings[0]);
            setInferedAssets(d.data.columnMappings[1]);
            setfileJSON(d.data.columnMappings[1]);
            setfile(d.data.columnMappings[2]);
            setFileName(d.data.document.name);
            setType(d.data.assetType);
            setInitial(false);
            setFirst(false);
            setFetchingUploadFile(false);
          })
          .catch(() => {
            setFetchingUploadFile(false);
          });
      }
    }
  }, [id]);

  const next = () => {
    if (!id || isEmpty(states)) return;

    const result = map(assetsFromFile, (asset) => ({
      ...asset,
      stateId:
        find(states, (state) => {
          return state.abbreviation === get(asset, 'state') || state.name.toLowerCase() === get(asset, 'state')?.toLowerCase();
        })?.id ?? null,
      state:
        find(states, (state) => {
          return state.abbreviation === get(asset, 'state') || state.name.toLowerCase() === get(asset, 'state')?.toLowerCase();
        })?.abbreviation ??
        '',
    }));

    setMovingNextStep(true);
    updateUploadFile(id, {items: formatAssets(result)})
      .then((d) => {
        const errorMessage = getErrorMessage(d);
        if (errorMessage) {
          toastRef.current?.showErrorToast(errorMessage);
        } else {
          toastRef.current?.showSuccessToast('Update successfully');
          navigate(`/assets/assetsReview/${id}`);
        }
        setMovingNextStep(false);
      })
      .catch((e) => {
        toastRef.current?.showErrorToast(getErrorMessage(e));
        setMovingNextStep(false);
      });
  };

  return (
    <div className={style['main-content']}>
      {/* Braedcrumb */}
      <Breadcrumb items={breadcrumb} />
      <ToastMessage ref={toastRef} className={style['toast']} />
      <div className={style['main']}>
        {/* Steps */}
        <StepsBar step={2} steps={['Upload', 'Map', 'Review']} />
        {/* Add property section */}
        <div className={style['section']}>
          <h2>
            <TooltipWrapper
              tooltipSection={TOOLTIP_SECTIONS.PageTitle}
              tooltipKey='Map Asset File'
            >
              <span>
                {sessionStorage.getItem('assetType')
                  ? `Map ${type} File`
                  : `Map Asset File`}
              </span>
            </TooltipWrapper>
          </h2>
          <div className={style['results']}>
            <div className={style['buttons']}>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Download template and formatting guide'
              >
                <button
                  className='secondary'
                  onClick={download}
                  disabled={downloading}
                >
                  Download template and formatting guide
                  {downloading ? (
                    <LoaderIndicator
                      className='button-loading'
                      loading={true}
                    />
                  ) : null}
                </button>
              </TooltipWrapper>
            </div>
            <div className='mappedfile-result'>
              <ul>
                <li className={style['mappedFileColumn1']}>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Uploaded file'
                  >
                    <span>Uploaded file</span>
                  </TooltipWrapper>
                  <p>
                    <i className={'icon'}></i>
                    <span className={style['mappedFileName']}>{fileName}</span>
                  </p>
                </li>
                <li className={style['mappedFileColumn2']}>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Mapped columns'
                  >
                    <span>Mapped columns</span>
                  </TooltipWrapper>
                  <span className='block'>
                    {filter(file, ['status', 'Mapped']).length}
                  </span>
                </li>
                <li className={style['mappedFileColumn3']}>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Total file columns'
                  >
                    <span>Total file columns</span>
                  </TooltipWrapper>
                  <span className='block'>
                    {get(
                      {
                        Inventory: INVENTORYFILECOLUMNS.length,
                        Vehicle: VEHICLEFILECOLUMNS.length,
                        'Leased Vehicle': LEASEVEHICLEFILECOLUMNS.length,
                        'Leased Equipment': LEASEEQUIPMENTFILECOLUMNS.length,
                      },
                      type,
                      FIXEDASSETFILECOLUMNS.length,
                    )}
                  </span>
                </li>
                <li className={style['mappedFileColumn4']}>
                  <TooltipWrapper
                    tooltipSection={TOOLTIP_SECTIONS.Common}
                    tooltipKey='Total file rows'
                  >
                    <span>Total file rows</span>
                  </TooltipWrapper>
                  <span className='block'>{initialAssets.length}</span>
                </li>
              </ul>
              <TooltipWrapper
                tooltipSection={TOOLTIP_SECTIONS.PageAction}
                tooltipKey='Remove & Replace file'
              >
                <button
                  onClick={() => {
                    if (id) {
                      setCancellingUploadFile(true);
                      cancelUploadFile(id)
                        .then((d) => {
                          const errorMessage = getErrorMessage(d);
                          if (errorMessage) {
                            toastRef.current?.showErrorToast(errorMessage);
                          } else {
                            toastRef.current?.showSuccessToast(
                              'Remove & Replace file successfully',
                            );
                            navigate('/assets/assetsUpload');
                          }
                          setCancellingUploadFile(false);
                        })
                        .catch((e) => {
                          toastRef.current?.showErrorToast(getErrorMessage(e));
                          setCancellingUploadFile(false);
                        });
                    }
                  }}
                  className='primary-link'
                  disabled={cancellingUploadFile}
                  data-testid='RemoveReplaceFileButton'
                >
                  Remove & Replace file
                  {cancellingUploadFile ? (
                    <LoaderIndicator
                      className='button-loading'
                      loading={true}
                    />
                  ) : null}
                </button>
              </TooltipWrapper>
            </div>
          </div>
        </div>
        {/* Added Properties */}
        <div className={style['section']}>
          <div className={classNames('table-layout', style['table-layout'])}>
            <table>
              {/* Table header */}
              <thead>
                <tr>
                  <th>
                    <TooltipWrapper
                      tooltipSection={TOOLTIP_SECTIONS.TableHeader}
                      tooltipKey='Status'
                    >
                      <span
                        className={classNames(
                          'sort',
                          isEqual(orderkey, 'status') && order,
                        )}
                        onClick={() => changeOrderHandle('status')}
                      >
                        Status
                      </span>
                    </TooltipWrapper>
                  </th>
                  <th>
                    <TooltipWrapper
                      tooltipSection={TOOLTIP_SECTIONS.TableHeader}
                      tooltipKey='Mapping priority'
                    >
                      <span
                        className={classNames(
                          'sort',
                          isEqual(orderkey, 'mappingPriority') && order,
                        )}
                        onClick={() => changeOrderHandle('mappingPriority')}
                      >
                        Mapping priority
                      </span>
                    </TooltipWrapper>
                  </th>
                  <th>
                    <TooltipWrapper
                      tooltipSection={TOOLTIP_SECTIONS.TableHeader}
                      tooltipKey='Avalara field name'
                    >
                      <span
                        className={classNames(
                          'sort',
                          isEqual(orderkey, 'propertyData') && order,
                        )}
                        onClick={() => changeOrderHandle('propertyData')}
                      >
                        Avalara field name
                      </span>
                    </TooltipWrapper>
                  </th>
                  <th>
                    <TooltipWrapper
                      tooltipSection={TOOLTIP_SECTIONS.TableHeader}
                      tooltipKey='Column name in file'
                    >
                      <span>Column name in file</span>
                    </TooltipWrapper>
                  </th>
                  <th>
                    <TooltipWrapper
                      tooltipSection={TOOLTIP_SECTIONS.TableHeader}
                      tooltipKey='Expected format'
                    >
                      <span
                        className={classNames(
                          'sort',
                          isEqual(orderkey, 'expectData') && order,
                        )}
                        onClick={() => changeOrderHandle('expectData')}
                      >
                        Expected format
                      </span>
                    </TooltipWrapper>
                  </th>
                  <th>
                    <div className={style['tip-column']}>
                      <TooltipWrapper
                        tooltipSection={TOOLTIP_SECTIONS.TableHeader}
                        tooltipKey='Column data preview'
                      >
                        <span>Column data preview</span>
                      </TooltipWrapper>{' '}
                      <i className={style['tooltip']}>
                        <span className={style['tip']}>
                          Invalid values will be removed/truncated
                        </span>
                      </i>
                    </div>
                  </th>
                </tr>
              </thead>
              {/* Table body */}
              <tbody>
                {file.map((column, index) => (
                  <tr key={`${index}-${column['propertyData']}`}>
                    <td>
                      <span
                        className={classNames(
                          'badge-status',
                          isEqual(column['status'], 'Mapped')
                            ? 'success'
                            : 'default',
                        )}
                      >
                        {column['status']}
                      </span>
                    </td>
                    <td>
                      <span
                        className={classNames(
                          'badge-status',
                          get(
                            {
                              Required: 'error error-required',
                              Recommended: 'warning',
                            },
                            column['mappingPriority'],
                            'normal',
                          ),
                        )}
                      >
                        {column['mappingPriority']}
                      </span>
                    </td>
                    <td>{column['propertyData']}</td>
                    <td>
                      <FieldSelect
                        labelText=''
                        options={fileColumns.map((item) => ({
                          name: item,
                          label: item,
                          value: item,
                        }))}
                        selectText={get(column, 'selectedColumn')}
                        onSelect={(value) => {
                          const fileResult = [
                            ...slice(file, 0, index),
                            assign(column, {
                              selectedColumn: value?.name,
                              status: value?.name ? 'Mapped' : 'Unmapped',
                            }),
                            ...slice(file, index + 1),
                          ];
                          setfile(fileResult);
                          const result = map(initialAssets, (item, fieldIdx) =>
                            assign(
                              item,
                              map(initialAssets, (field) => {
                                const obj = pick(
                                  field,
                                  value?.name as string,
                                );
                                return mapKeys(
                                  obj,
                                  () => column['propertyData'],
                                );
                              })[fieldIdx],
                            ),
                          );
                          setInferedAssets(result);
                        }}
                      />
                    </td>
                    <td>{column['expectData']}</td>
                    <td>
                      <div className={style['blocks']}>
                        {_(initialAssets)
                          .filter((item) =>
                            has(item, get(column, 'selectedColumn')),
                          )
                          .uniqBy(get(column, 'selectedColumn'))
                          .take(3)
                          .map((item, fieldIdx) => (
                            <span key={fieldIdx}>
                              {get(item, get(column, 'selectedColumn'))}
                            </span>
                          ))
                          .value()}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={style['buttons']}>
            <TooltipWrapper
              tooltipSection={TOOLTIP_SECTIONS.PageAction}
              tooltipKey='Next'
            >
              <button
                className='primary'
                disabled={movingNextStep || !validate}
                onClick={next}
                data-testid='NextButton'
              >
                Next
                {movingNextStep ? (
                  <LoaderIndicator className='button-loading' loading={true} />
                ) : null}
              </button>
            </TooltipWrapper>
            <TooltipWrapper
              tooltipSection={TOOLTIP_SECTIONS.PageAction}
              tooltipKey='Cancel import'
            >
              <button
                className='secondary'
                onClick={() => {
                  if (id) {
                    setCancellingUploadFile(true);
                    cancelUploadFile(id)
                      .then(() => {
                        navigate('/assets/assetsUpload');
                      })
                      .then(() => {
                        setCancellingUploadFile(false);
                      });
                  }
                }}
              >
                Cancel import
                {cancellingUploadFile ? (
                  <LoaderIndicator className='button-loading' loading={true} />
                ) : null}
              </button>
            </TooltipWrapper>
          </div>
        </div>
      </div>
      <Loader
        isOpen={loadingProperties || updatingUploadFile || fetchingUploadFile}
      />
    </div>
  );
}

const mapStateToProps = (state: ReduxState) => ({
  states: state.states,
  uploadAssetsFile: state.uploadAssetsFile,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setUploadAssetsFile: (value: File | undefined) =>
    dispatch(actions.setUploadAssetsFile(value)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AssetsMapFile);
